import * as z$1 from "zod/v4";
//#region src/navigation-action.ts
const nonEmptyStringSchema = z$1.string().trim().min(1);
const LOCAL_WEB_URL = "http://localhost";
const TRAILING_SLASH_PATTERN = /\/+$/u;
const computerSetupModeSchema = z$1.enum([
	"setup",
	"reconnect",
	"update_service"
]);
const openMeldWebUrlSchema = z$1.url().refine((value) => {
	const protocol = new URL(value).protocol;
	return protocol === "http:" || protocol === "https:";
}, "OpenMeld Web URL must use HTTP or HTTPS.");
const computerSetupNavigationTargetSchema = z$1.discriminatedUnion("mode", [
	z$1.strictObject({
		kind: z$1.literal("computer_setup"),
		mode: z$1.literal("setup")
	}),
	z$1.strictObject({
		connectedComputerId: nonEmptyStringSchema,
		kind: z$1.literal("computer_setup"),
		mode: z$1.literal("reconnect")
	}),
	z$1.strictObject({
		connectedComputerId: nonEmptyStringSchema,
		kind: z$1.literal("computer_setup"),
		mode: z$1.literal("update_service")
	})
]);
const openMeldNavigationTargetSchema = z$1.union([
	z$1.strictObject({
		kind: z$1.literal("organization_app"),
		service: z$1.literal("github")
	}),
	z$1.strictObject({
		kind: z$1.literal("space"),
		spaceId: nonEmptyStringSchema
	}),
	z$1.strictObject({ kind: z$1.literal("projects") }),
	z$1.strictObject({
		kind: z$1.literal("project"),
		projectId: nonEmptyStringSchema
	}),
	z$1.strictObject({ kind: z$1.literal("slack_identity_link") }),
	z$1.strictObject({
		connectToUserId: nonEmptyStringSchema.optional(),
		kind: z$1.literal("message_channel_identity_link"),
		purpose: z$1.enum(["continue_request", "optional_setup"]),
		requestedAction: nonEmptyStringSchema.max(160).optional()
	}),
	computerSetupNavigationTargetSchema
]);
const navigationActionSchema = z$1.strictObject({
	label: nonEmptyStringSchema,
	target: openMeldNavigationTargetSchema,
	url: openMeldWebUrlSchema
});
function buildOrganizationRootPath(orgSlug) {
	return `/o/${encodePathSegment(nonEmptyStringSchema.parse(orgSlug))}`;
}
function buildOrganizationPluginsPath(orgSlug) {
	return `${buildOrganizationRootPath(orgSlug)}/plugins`;
}
function buildOrganizationPluginPath(input) {
	return `${buildOrganizationPluginsPath(input.orgSlug)}/${encodePathSegment(nonEmptyStringSchema.parse(input.service))}`;
}
function buildOrganizationSpacePath(input) {
	const url = new URL(`${buildOrganizationRootPath(input.orgSlug)}/spaces/${encodePathSegment(nonEmptyStringSchema.parse(input.spaceId))}`, LOCAL_WEB_URL);
	for (const [key, value] of Object.entries(input.search ?? {})) if (typeof value === "string" && value.trim().length > 0) url.searchParams.set(key, value);
	return `${url.pathname}${url.search}`;
}
function buildOpenMeldOrganizationWebUrl(input) {
	const organizationSlug = nonEmptyStringSchema.parse(input.organizationSlug);
	const url = parseHttpUrl(input.webAppUrl, "Web app URL");
	url.hash = "";
	url.pathname = buildOrganizationRootPath(organizationSlug);
	url.search = "";
	return url.toString();
}
function buildOpenMeldNavigationAction(input) {
	const target = openMeldNavigationTargetSchema.parse(input.target);
	const url = parseHttpUrl(input.organizationWebUrl, "Organization Web URL");
	url.hash = "";
	url.search = "";
	const organizationPath = url.pathname.replace(TRAILING_SLASH_PATTERN, "");
	let label;
	switch (target.kind) {
		case "organization_app":
			label = "Open GitHub access";
			url.pathname = `${organizationPath}/center-agent/plugins/${encodePathSegment(target.service)}`;
			break;
		case "space":
			label = "Open this Space";
			url.pathname = `${organizationPath}/spaces/${encodePathSegment(target.spaceId)}`;
			break;
		case "projects":
			label = "Open Projects";
			url.pathname = `${organizationPath}/projects`;
			break;
		case "project":
			label = "Open this Project";
			url.pathname = `${organizationPath}/projects/${encodePathSegment(target.projectId)}`;
			break;
		case "slack_identity_link": throw new TypeError("Slack identity-link actions must be issued by OpenMeld Core.");
		case "message_channel_identity_link": throw new TypeError("Message Channel identity-link actions must be issued by OpenMeld Core.");
		case "computer_setup":
			url.pathname = `${organizationPath}/connect`;
			if (target.mode === "setup") {
				label = "Connect this computer";
				break;
			}
			url.searchParams.set("connectedComputerId", target.connectedComputerId);
			url.searchParams.set("session", "fresh");
			if (target.mode === "reconnect") {
				label = "Reconnect this computer";
				break;
			}
			label = "Update OpenMeld on Computer";
			url.searchParams.set("serviceAction", "update_service");
			url.searchParams.sort();
			break;
		default: return unreachableNavigationTarget(target);
	}
	return navigationActionSchema.parse({
		label,
		target,
		url: url.toString()
	});
}
function buildSlackIdentityLinkNavigationAction(input) {
	nonEmptyStringSchema.parse(input.organizationName);
	const token = nonEmptyStringSchema.parse(input.token);
	const url = parseHttpUrl(input.webAppUrl, "Web app URL");
	url.hash = "";
	url.pathname = "/link-slack";
	url.search = "";
	url.searchParams.set("token", token);
	return navigationActionSchema.parse({
		label: input.purpose === "optional_setup" ? "Connect my account" : "Connect my account and continue",
		target: {
			kind: "message_channel_identity_link",
			purpose: input.purpose ?? "continue_request"
		},
		url: url.toString()
	});
}
function unreachableNavigationTarget(target) {
	throw new TypeError(`Unsupported OpenMeld navigation target: ${target}`);
}
function encodePathSegment(value) {
	return encodeURIComponent(value);
}
function parseHttpUrl(value, label) {
	const result = openMeldWebUrlSchema.safeParse(value);
	if (!result.success) throw new TypeError(`${label} must use HTTP or HTTPS.`);
	return new URL(result.data);
}
//#endregion
export { buildOpenMeldNavigationAction, buildOpenMeldOrganizationWebUrl, buildOrganizationPluginPath, buildOrganizationPluginsPath, buildOrganizationRootPath, buildOrganizationSpacePath, buildSlackIdentityLinkNavigationAction, computerSetupModeSchema, computerSetupNavigationTargetSchema, navigationActionSchema, openMeldNavigationTargetSchema, openMeldWebUrlSchema };

//# sourceMappingURL=navigation-action.js.map